// ─── Templates repository page ──────────────────────────────────────────────
//
// The dedicated sidebar home for the shared template repository (see
// emailTemplates.js + server.js's /api/templates + db.js's Template model).
// Create/edit/delete happens here, using the same rich-text editor (Quill +
// WordRibbon) every other compose surface in the app already uses, so a
// template looks and behaves exactly like the email it'll become. Mail
// Merge, Sequences, and Workspace each read this same repository and filter
// by category ("fresh" vs "followup" vs "general") to suggest the right
// template for what the user is currently composing — this page is where
// that categorization actually gets set.
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { FileText, Plus, Search, Copy, Trash2, Pencil, X, Minus, Maximize2, Minimize2, ChevronLeft, ChevronRight } from "lucide-react";
import RichTextEditor from "./richtext/RichTextEditor";
import WordRibbon from "./richtext/WordRibbon";
import "react-quill/dist/quill.snow.css";
import { TEMPLATE_CATEGORIES, fetchTemplateRepo, createTemplateRepo, updateTemplateRepo, deleteTemplateRepo } from "./emailTemplates";
import { C } from "./ui/theme";

// Phase 3 (standalone page-file token migration, see linear-brewing-toast.md)
// — this file used to define its own local Fluent/Azure-blue palette here
// (`const T = {...}`, accent:"#0078D4"), completely separate from
// ui/theme.js's Apple palette `C`. Every `C.xxx` reference below has been
// renamed to `C.xxx` (a pure token-name swap, zero value remapping) and the
// local palette object removed.

const CATEGORY_STYLE = {
  fresh:    { bg: C.accentMuted, tc: C.accent },
  followup: { bg: C.purpleMuted, tc: C.purple },
  general:  { bg: C.bgDeep,      tc: C.textSub },
};

function CategoryBadge({ category }) {
  const cfg = CATEGORY_STYLE[category] || CATEGORY_STYLE.general;
  const label = TEMPLATE_CATEGORIES.find(c => c.id === category)?.label || "General";
  return <span style={{ background: cfg.bg, color: cfg.tc, fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 99, whiteSpace: "nowrap" }}>{label}</span>;
}

function stripHtml(html) {
  return (html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

// ─── UI/UX-only additions below ─────────────────────────────────────────────
// Everything in this section is presentation: window sizing/chrome for the
// New/Edit Template dialog, and a horizontal-scroll safety net for its
// ribbon. None of it touches save/load, Quill's editor instance, template
// data, categories, or any API call — see TemplateEditorModal's own save()
// and state below, which are unchanged.

// One-time scoped CSS so the template editor's own Quill canvas can flex to
// fill whatever height the window currently has (bigger in "maximized",
// smaller in the default "normal" size) instead of a fixed pixel height.
// Scoped to the "sdr-template-editor-canvas" class only — this can never
// affect Mail Merge's, Workspace's, or Sequences' own RichTextEditor
// instances, since none of them carry that class name.
let templateEditorCssInjected = false;
function injectTemplateEditorCss() {
  if (templateEditorCssInjected || typeof document === "undefined") return;
  templateEditorCssInjected = true;
  const style = document.createElement("style");
  style.setAttribute("data-sdr-template-editor", "1");
  // Quill's own stylesheet (quill.core.css, loaded globally) already gives
  // .ql-editor `height:100%; overflow-y:auto;` — Quill's documented,
  // tested way to make the canvas scroll is for ITS PARENT (.ql-container)
  // to have a real, definite height, which .ql-editor then fills via that
  // 100%. The first version of this file instead also put `flex:1` +
  // `overflow-y:auto` directly on .ql-editor AND made .ql-container its
  // own flex column — two different sizing systems fighting over the same
  // element — which is what produced the "rigid, no scrollbar" canvas.
  // Fix: only .ql-container gets `flex:1` (a plain block box that flexbox
  // still gives a definite content height, satisfying .ql-editor's
  // `height:100%`); .ql-editor itself is left exactly as Quill ships it.
  style.textContent =
    ".sdr-template-editor-canvas.quill{display:flex;flex-direction:column;flex:1;min-height:0;}" +
    ".sdr-template-editor-canvas.quill .ql-container{flex:1;min-height:0;}" +
    ".sdr-ribbon-scroll{scrollbar-width:thin;}" +
    ".sdr-ribbon-scroll::-webkit-scrollbar{height:7px;}" +
    ".sdr-ribbon-scroll::-webkit-scrollbar-thumb{background:#d8dce6;border-radius:4px;}";
  document.head.appendChild(style);
}

// Wraps the ribbon (WordRibbon, unmodified) in a strictly non-shrinking,
// horizontally-scrollable track instead of letting flex compress its groups
// — that compression is what produced the overlapping/mixed-together look
// in the original bug report. `minWidth:"max-content"` on the inner track
// forces every ribbon group to render at its natural width always; when
// that's wider than the available space, this shows chevrons and scrolls
// instead of squeezing anything. Purely a wrapper — WordRibbon.js itself is
// untouched, so every other surface that renders it is unaffected.
function RibbonScrollArea({ children }) {
  const scrollerRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);
  const check = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 2);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);
  useEffect(() => {
    check();
    const el = scrollerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(check);
    ro.observe(el);
    window.addEventListener("resize", check);
    return () => { ro.disconnect(); window.removeEventListener("resize", check); };
  }, [check]);
  const scrollByPx = (dx) => scrollerRef.current?.scrollBy({ left: dx, behavior: "smooth" });
  const chevronBtn = (side) => ({
    position: "absolute", top: 0, bottom: 0, [side]: 0, zIndex: 2, width: 26,
    display: "flex", alignItems: "center", justifyContent: "center", border: "none", cursor: "pointer",
    background: side === "left"
      ? "linear-gradient(90deg,#F8F7F6 55%,rgba(248,247,246,0))"
      : "linear-gradient(270deg,#F8F7F6 55%,rgba(248,247,246,0))",
    color: "#4A5078",
  });
  return (
    <div style={{ position: "relative", display: "flex", alignItems: "stretch" }}>
      {canLeft && <button onClick={() => scrollByPx(-260)} title="Scroll left" style={chevronBtn("left")}><ChevronLeft size={14} /></button>}
      <div ref={scrollerRef} className="sdr-ribbon-scroll" style={{ overflowX: "auto", overflowY: "hidden", flex: 1 }} onScroll={check}>
        <div style={{ display: "inline-flex", alignItems: "stretch", minWidth: "max-content" }}>{children}</div>
      </div>
      {canRight && <button onClick={() => scrollByPx(260)} title="Scroll right" style={chevronBtn("right")}><ChevronRight size={14} /></button>}
    </div>
  );
}

function TemplateEditorModal({ template, onClose, onSaved, addToast }) {
  const isNew = !template?.id;
  const [name, setName] = useState(template?.name || "");
  const [category, setCategory] = useState(template?.category || "general");
  const [subject, setSubject] = useState(template?.subject || "");
  const [body, setBody] = useState(template?.body || "<p><br></p>");
  const [saving, setSaving] = useState(false);
  const [quillEl, setQuillEl] = useState(null);
  const getQuill = useCallback(() => quillEl, [quillEl]);

  // Window chrome state only — "normal" | "maximized" | "minimized". This
  // never unmounts the editor: all three states render the exact same DOM
  // (see the always-mounted wrapper below), so the Quill instance, its ref,
  // and anything already typed survive switching between them intact.
  // Defaults to "maximized" (not "normal") — the previous default size
  // still left the canvas cramped on smaller/laptop-height screens (the
  // actual bug reported: "too small, not able to see entire text"). Opening
  // full-size by default is also just how Word/Outlook's own compose
  // windows behave; the user can still restore/minimize freely from here.
  const [winState, setWinState] = useState("maximized");
  // Remembers whichever state (maximized/normal) was active right before
  // minimizing, so Restore returns to THAT instead of always snapping back
  // to "normal" — minimizing a maximized window and restoring it should
  // give you the maximized window back, same as any real OS window.
  const prevWinState = useRef("maximized");
  useEffect(() => { injectTemplateEditorCss(); }, []);
  const isMax = winState === "maximized";
  const isMin = winState === "minimized";

  const save = async () => {
    if (!name.trim()) { addToast?.("Give the template a name first", "error"); return; }
    setSaving(true);
    try {
      if (isNew) {
        await createTemplateRepo({ name: name.trim(), category, subject, body });
        addToast?.("Template saved", "success");
      } else {
        await updateTemplateRepo(template.id, { name: name.trim(), category, subject, body });
        addToast?.("Template updated", "success");
      }
      onSaved();
    } catch (e) {
      addToast?.(e.message || "Could not save template", "error");
    } finally {
      setSaving(false);
    }
  };

  // Sizing per window state. "normal" is a real desktop-editor default (not
  // a cramped dialog) that still clamps to the viewport with vw/vh so it
  // adapts across screen resolutions automatically. "maximized" goes
  // (almost) edge-to-edge. Both are just CSS on the same always-mounted
  // dialog below — nothing here affects save/load or the editor itself.
  const dialogSize = isMax
    ? { width: "calc(100vw - 24px)", height: "calc(100vh - 24px)" }
    : { width: "min(1280px, 96vw)", height: "min(860px, 92vh)" };

  const winBtnStyle = { background: "transparent", border: "none", cursor: "pointer", color: C.textSub, padding: 6, borderRadius: 5, display: "flex", alignItems: "center", justifyContent: "center" };

  return (
    <>
      {/* Backdrop + dialog stay mounted in every window state — minimizing
          only hides this wrapper visually (display:none below), so the
          Quill instance/ref and any unsaved text survive minimize/restore
          exactly as if the dialog were simply scrolled off-screen. */}
      <div
        style={{ position: "fixed", inset: 0, background: isMin ? "transparent" : "rgba(16,24,40,.5)", zIndex: 400, display: isMin ? "none" : "flex", alignItems: "center", justifyContent: "center", padding: isMax ? 12 : 20, pointerEvents: isMin ? "none" : "auto" }}
        onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div style={{ background: C.bgCard, borderRadius: isMax ? 8 : 10, ...dialogSize, display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(16,24,40,.3)", overflow: "hidden" }}>
          <div style={{ padding: "14px 18px", borderBottom: "1px solid " + C.border, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
            {/* Shows whatever's typed in the Name field below, live — not a
                generic "New Template"/"Edit Template" label — so the title
                bar always reflects the actual template you're working on.
                The pill keeps the New/Editing signal that the old label
                used to carry on its own. */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name.trim() || "Untitled Template"}</h3>
              <span style={{ flexShrink: 0, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".4px", padding: "2px 8px", borderRadius: 99, background: isNew ? C.accentMuted : C.greenMuted, color: isNew ? C.accent : C.green }}>{isNew ? "New" : "Editing"}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
              <button onClick={() => { prevWinState.current = winState; setWinState("minimized"); }} title="Minimize" style={winBtnStyle}><Minus size={15} /></button>
              <button onClick={() => setWinState(isMax ? "normal" : "maximized")} title={isMax ? "Restore" : "Maximize"} style={winBtnStyle}>
                {isMax ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
              </button>
              <button onClick={onClose} title="Close" style={{ ...winBtnStyle, color: "#A4262C" }}><X size={18} /></button>
            </div>
          </div>
          {/* Ribbon comes FIRST, directly under the title bar — a fixed
              command bar at the top of the window, exactly where Word/
              Outlook put theirs, instead of buried below the metadata
              fields. Name/Use for/Subject collapse into one compact row
              right under it, and the canvas takes essentially everything
              else — the layout the window's actual job (writing/editing
              the template body) deserves the most room for. */}
          <div style={{ background: "#F8F7F6", borderBottom: "1px solid " + C.border, padding: "2px 4px", flexShrink: 0 }}>
            <RibbonScrollArea>
              <WordRibbon getQuill={getQuill} showStyles addToast={addToast} docFilename={"template-" + (name || "untitled")} />
            </RibbonScrollArea>
          </div>
          <div style={{ padding: "10px 20px", display: "flex", gap: 12, flexShrink: 0, borderBottom: "1px solid " + C.border, flexWrap: "wrap", rowGap: 10 }}>
            <div style={{ flex: 2, minWidth: 160 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: C.textSub, display: "block", marginBottom: 3 }}>Name</label>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Cold outreach — SaaS founders" style={{ width: "100%", padding: "7px 10px", border: "1px solid " + C.border, borderRadius: 6, fontSize: 13, boxSizing: "border-box" }} />
            </div>
            <div style={{ flex: 1, minWidth: 120 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: C.textSub, display: "block", marginBottom: 3 }}>Use for</label>
              <select value={category} onChange={e => setCategory(e.target.value)} style={{ width: "100%", padding: "7px 10px", border: "1px solid " + C.border, borderRadius: 6, fontSize: 13, background: "#fff" }}>
                {TEMPLATE_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div style={{ flex: 3, minWidth: 200 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: C.textSub, display: "block", marginBottom: 3 }}>Subject</label>
              <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject line (merge tags like {{FirstName}} work here too)" style={{ width: "100%", padding: "7px 10px", border: "1px solid " + C.border, borderRadius: 6, fontSize: 13, boxSizing: "border-box" }} />
            </div>
          </div>
          {/* The canvas is now the only flexible region in the whole
              window — everything else above (title bar, ribbon, meta row)
              is flexShrink:0 (fixed height), and the footer below is too,
              so 100% of whatever height Maximize/Restore/Minimize leaves
              over goes here. Still exactly one scroll region (.ql-editor's
              own overflow-y:auto, per the earlier scrollbar fix) — long
              template bodies scroll fully into view instead of clipping. */}
          <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <RichTextEditor
              ref={r => { const ed = r?.getEditor?.(); if (ed) setQuillEl(ed); }}
              value={body}
              onChange={setBody}
              className="sdr-template-editor-canvas"
              style={{ flex: 1, minHeight: 0 }}
            />
          </div>
          <div style={{ padding: "12px 20px", borderTop: "1px solid " + C.border, display: "flex", justifyContent: "flex-end", gap: 8, flexShrink: 0 }}>
            <button onClick={onClose} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid " + C.border, background: "#fff", color: C.textSub, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
            <button onClick={save} disabled={saving} style={{ padding: "8px 16px", borderRadius: 6, border: "none", background: C.accent, color: "#fff", fontSize: 13, fontWeight: 600, cursor: saving ? "default" : "pointer", opacity: saving ? .7 : 1 }}>{saving ? "Saving..." : "Save Template"}</button>
          </div>
        </div>
      </div>

      {/* Minimized state — a compact docked bar, standard OS-taskbar style.
          Clicking it (or its restore icon) just flips winState back to
          "normal"; the dialog above was never unmounted, so it reappears
          exactly as it was left. */}
      {isMin && (
        <div
          onClick={() => setWinState(prevWinState.current)}
          style={{ position: "fixed", right: 20, bottom: 20, zIndex: 401, background: C.bgCard, border: "1px solid " + C.border, borderRadius: 8, boxShadow: "0 8px 24px rgba(16,24,40,.25)", padding: "10px 12px", display: "flex", alignItems: "center", gap: 10, cursor: "pointer", minWidth: 220 }}
        >
          <FileText size={15} color={C.accent} />
          <span style={{ fontSize: 13, fontWeight: 600, color: C.text, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name.trim() || "Untitled Template"}</span>
          <button onClick={e => { e.stopPropagation(); setWinState(prevWinState.current); }} title="Restore" style={winBtnStyle}><Maximize2 size={13} /></button>
          <button onClick={e => { e.stopPropagation(); onClose(); }} title="Close" style={{ ...winBtnStyle, color: "#A4262C" }}><X size={14} /></button>
        </div>
      )}
    </>
  );
}

export default function TemplatesPage({ addToast }) {
  const toast = addToast || function () {};
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [editing, setEditing] = useState(null); // template being edited, or {} for new
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    fetchTemplateRepo().then(list => setTemplates(Array.isArray(list) ? list : [])).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);
  // Cross-window live updates — Mail Merge/Workspace popups write to the
  // same localStorage cache on every fetch/save (see emailTemplates.js).
  useEffect(() => {
    const onStorage = e => { if (e.key === "sdr_email_templates") load(); };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates.filter(t => {
      if (categoryFilter !== "all" && (t.category || "general") !== categoryFilter) return false;
      if (!q) return true;
      return (t.name || "").toLowerCase().includes(q) || (t.subject || "").toLowerCase().includes(q) || stripHtml(t.body).toLowerCase().includes(q);
    });
  }, [templates, search, categoryFilter]);

  const duplicate = async (t) => {
    try {
      await createTemplateRepo({ name: t.name + " (copy)", category: t.category, subject: t.subject, body: t.body });
      toast("Template duplicated", "success");
      load();
    } catch (e) { toast(e.message || "Could not duplicate template", "error"); }
  };

  const remove = async (t) => {
    try {
      await deleteTemplateRepo(t.id);
      toast("Template deleted", "success");
      setConfirmDelete(null);
      load();
    } catch (e) { toast(e.message || "Could not delete template", "error"); }
  };

  return (
    <div style={{ maxWidth: 1100 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: C.text, display: "flex", alignItems: "center", gap: 8 }}><FileText size={22} color={C.accent} /> Templates</h1>
          <p style={{ fontSize: 13, color: C.textSub, marginTop: 2 }}>One shared library — used from Mail Merge, Sequences, and Workspace to start fresh emails or follow-ups.</p>
        </div>
        <button onClick={() => setEditing({})} style={{ display: "flex", alignItems: "center", gap: 6, background: C.accent, color: "#fff", border: "none", borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}><Plus size={15} /> New Template</button>
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ position: "relative", flex: "1 1 240px", maxWidth: 320 }}>
          <Search size={14} color={C.textMuted} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)" }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search templates..." style={{ width: "100%", padding: "8px 10px 8px 30px", border: "1px solid " + C.border, borderRadius: 8, fontSize: 13, boxSizing: "border-box" }} />
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {[{ id: "all", label: "All" }, ...TEMPLATE_CATEGORIES].map(c => (
            <button key={c.id} onClick={() => setCategoryFilter(c.id)} style={{ padding: "6px 12px", borderRadius: 99, border: "1px solid " + (categoryFilter === c.id ? C.accent : C.border), background: categoryFilter === c.id ? C.accentMuted : "#fff", color: categoryFilter === c.id ? C.accent : C.textSub, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{c.label}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <p style={{ color: C.textMuted, fontSize: 13 }}>Loading templates...</p>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 20px", background: C.bgCard, border: "1px solid " + C.border, borderRadius: 10 }}>
          <FileText size={36} color={C.textMuted} style={{ marginBottom: 10 }} />
          <p style={{ fontSize: 14, color: C.textSub, marginBottom: 4 }}>{templates.length === 0 ? "No templates yet" : "No templates match your search"}</p>
          {templates.length === 0 && <p style={{ fontSize: 12, color: C.textMuted, marginBottom: 14 }}>Create one here, or save one from Mail Merge/Sequences — it'll show up in this list.</p>}
          {templates.length === 0 && <button onClick={() => setEditing({})} style={{ background: C.accent, color: "#fff", border: "none", borderRadius: 8, padding: "9px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Create your first template</button>}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }}>
          {filtered.map(t => (
            <div key={t.id} style={{ background: C.bgCard, border: "1px solid " + C.border, borderRadius: 10, padding: 14, display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
                <p style={{ fontSize: 14, fontWeight: 700, color: C.text, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.name}</p>
                <CategoryBadge category={t.category} />
              </div>
              <p style={{ fontSize: 12, color: C.textSub, minHeight: 16, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.subject || "(no subject)"}</p>
              <p style={{ fontSize: 12, color: C.textMuted, lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden", minHeight: 54 }}>{stripHtml(t.body) || "(empty)"}</p>
              <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                <button onClick={() => setEditing(t)} title="Edit" style={{ display: "flex", alignItems: "center", gap: 4, background: C.bgHover, border: "none", borderRadius: 6, padding: "6px 10px", fontSize: 12, color: C.accent, cursor: "pointer" }}><Pencil size={12} /> Edit</button>
                <button onClick={() => duplicate(t)} title="Duplicate" style={{ display: "flex", alignItems: "center", gap: 4, background: C.bgDeep, border: "none", borderRadius: 6, padding: "6px 10px", fontSize: 12, color: C.textSub, cursor: "pointer" }}><Copy size={12} /> Duplicate</button>
                <button onClick={() => setConfirmDelete(t)} title="Delete" style={{ display: "flex", alignItems: "center", gap: 4, background: "#FDE7E9", border: "none", borderRadius: 6, padding: "6px 10px", fontSize: 12, color: "#A4262C", cursor: "pointer", marginLeft: "auto" }}><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <TemplateEditorModal
          template={editing}
          addToast={toast}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {confirmDelete && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(16,24,40,.5)", zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center" }} onMouseDown={e => { if (e.target === e.currentTarget) setConfirmDelete(null); }}>
          <div style={{ background: C.bgCard, borderRadius: 10, padding: 20, width: 360, maxWidth: "90vw", boxSizing: "border-box", boxShadow: "0 20px 60px rgba(16,24,40,.3)" }}>
            <p style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 6 }}>Delete "{confirmDelete.name}"?</p>
            <p style={{ fontSize: 12, color: C.textSub, marginBottom: 16 }}>This removes it from the shared repository — Mail Merge, Sequences, and Workspace will no longer see it. This can't be undone.</p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button onClick={() => setConfirmDelete(null)} style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid " + C.border, background: "#fff", color: C.textSub, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
              <button onClick={() => remove(confirmDelete)} style={{ padding: "7px 14px", borderRadius: 6, border: "none", background: "#A4262C", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
